import { type ResolverContext } from "owa-graph-schema";
import type RequestOptions from "owa-service/lib/RequestOptions";
import { type DuplicateActionContext } from "../types/DuplicateActionContext";

/**
 *
 * @param requestOptions The request options to configure for duplicate action detection on the server.
 * @param context The resolver context that contains the duplicate action information (if enabled).
 * @param operationOffset a number between 0 and 100, in all known cases today it is 0.
 *
 * But, adding this parameter as a non-optional parameter for awareness:
 *
 * - Duplicate action detection is done server side at the service command level using the X-OWA-ActionId header to uniquely identify the action.
 * - The BaseActionId in the resolver context is guaranteed by the action queue to be unique per *graphQL operation*.
 * - But, GraphQL operations are not, *necessarily*, one-to-one with service commands.
 *
 * All queued action GraphQL operations, today, are one-to-one with OWS service commands, so this parameter should be 0.
 *
 * But, if a GraphQL operation contains more than one service command, and those commands need duplicate detection, the operation needs to add an offset to the BaseActionId to
 * ensure that each service command has a unique action id.  Moreover, that offset assignment must be persisted/durable across re-execution of the graphQL operation (e.g., if OWS command
 * serviceCallA is assigned offset 0 and serviceCallB is assigned offset 1, and the operation is retried by the queue, they must assigned those same offsets again -- even if
 * the execution is done in a different session).
 *
 * If a graphQL operation has only one mutation resolver, it's fairly straightforward to ensure the offsets are durable across rexecution: e.g., you'd need to make sure that even if there are
 * conditional statements that change the order of the service commands across operation executions (probably uncommon), the offset for each command is always the same.
 *
 * If a graphQL operation has more than one mutation resolver, the problem is a lot more complicated and would probably require leveraging the resolver "info" parameter
 * to ensure consistent ordering/offsets across the mutations.
 */
export function setDuplicateDetectionOptions(requestOptions: RequestOptions, context: ResolverContext, operationOffset: number): void {
    if (operationOffset < 0 || operationOffset > 99) {
        throw new Error("operationOffset must be between 0 and 99");
    }
    
    const duplicateActionInfo = (context as any as DuplicateActionContext).duplicateActionInfo;
    if (duplicateActionInfo) {
        const { BaseActionId, DeviceID, Protocol, DeviceType } = duplicateActionInfo;
        if (BaseActionId && DeviceID && Protocol && DeviceType) {
            let headers = requestOptions.headers;
            if (!headers) {
                headers = new Headers();
                requestOptions.headers = headers;
            }
            
            headers.append("X-OWA-ActionId", (BaseActionId + operationOffset).toString());
            headers.append("X-OWA-DeviceId", DeviceID);
            headers.append("X-OWA-Protocol", Protocol);
            headers.append("X-OWA-DeviceType", DeviceType);
        }
    }
}
